import React from 'react';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { EllipsisOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Dropdown, Button, Spin, Tooltip, App, Input } from 'antd';
import type { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { deleteExportBuilderAdvancedColumn } from '../../../actions/brand/export_builder_advanced/delete';
import { TableHeaderCell } from './EBPTableHeaderCell';
import { TableBodyCell } from './EBPTableBodyCell';

const ROW_HEIGHT = 48;
const COL_WIDTH = 200;

type EBPPreviewTableProps = {
  setDrawerVisible: (bool: boolean, colId: number | null, newCol?: boolean) => void;
  templateColumns: {
    id: number;
    label: string;
    number: number;
    dataKey: string;
    key: number;
  }[];
  updateColumnsOrder: (cols: any[]) => void;
  fetching: boolean;
  preview: any[];
  segment?: string;
  disabled?: boolean;
};

export interface DragIndexState {
  active: UniqueIdentifier;
  over: UniqueIdentifier | undefined;
  direction?: 'left' | 'right';
}

const EBPPreviewTable: React.FC<EBPPreviewTableProps> = ({
  setDrawerVisible,
  templateColumns,
  updateColumnsOrder,
  fetching,
  preview,
  segment,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [columnSearchTerm, setColumnSearchTerm] = React.useState('');

  const filteredColumns = React.useMemo(() => {
    if (!columnSearchTerm.trim()) {
      return templateColumns;
    }
    return templateColumns.filter(col =>
      col.label.toLowerCase().includes(columnSearchTerm.toLowerCase())
    );
  }, [templateColumns, columnSearchTerm]);

  const handleDeleteCol = (colId: number) => {
    modal.confirm({
      title: t('exportBuilderAdvanced:deleteColumnWarning'),
      icon: <ExclamationCircleOutlined />,
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk() {
        dispatch(deleteExportBuilderAdvancedColumn(colId));
      },
    });
  };

  const getRow = ({ index }: { index: number }) => {
    return Array.isArray(preview) && preview[index] ? preview[index] : {};
  };

  const columnItems = (colId: number) => {
    if (segment === 'loadSheets') {
      return [];
    }

    return [
      {
        label: t('exportBuilderAdvanced:editColumn'),
        key: 'ebp_edit-col',
        onClick: () => setDrawerVisible(true, colId),
      },
      ...(segment === 'exportBuilderAdvanced'
        ? [
            {
              label: t('exportBuilderAdvanced:addColumn'),
              key: 'ebp_add-col',
              onClick: () => setDrawerVisible(true, colId, true),
            },
            {
              label: t('exportBuilderAdvanced:deleteColumn'),
              key: 'ebp_delete-col',
              onClick: () => handleDeleteCol(colId),
            },
          ]
        : []),
    ];
  };

  const headerRenderer = ({ label, columnData }: any) => {
    return (
      <TableHeaderCell id={columnData.id} disabled={disabled}>
        <div className="flex items-center w-full">
          <div className="flex items-center mr-2 w-full overflow-hidden">
            <Tooltip title={label}>
              <span
                data-testid="preview-table-header-label"
                className="preview-table__header-title__truncate overflow-hidden overflow-ellipsis"
              >
                {label}
              </span>
            </Tooltip>
          </div>

          <Dropdown
            menu={{ items: columnItems(columnData.id) }}
            trigger={['click']}
            disabled={disabled}
          >
            <EllipsisOutlined />
          </Dropdown>
        </div>
      </TableHeaderCell>
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (disabled) return;

    if (active.id !== over?.id) {
      const activeIndex = templateColumns.findIndex(i => i.id === active?.id);
      const overIndex = templateColumns.findIndex(i => i.id === over?.id);

      const updatedRecord = templateColumns.map((col: any) => {
        return {
          ...col,
          number:
            active?.id === col.id
              ? templateColumns.find(i => i.id === active?.id)?.number
              : (over?.id === col.id && templateColumns.find(i => i.id === over?.id)?.number) ||
                col.number,
        };
      });
      const sortedCols = arrayMove(updatedRecord, activeIndex, overIndex);

      updateColumnsOrder(sortedCols);
      return sortedCols;
    }
  };

  if (!templateColumns) {
    return null;
  }

  if (fetching)
    return (
      <div className="h-full flex justify-center pt-8">
        <Spin />
      </div>
    );

  return (
    <div className="flex flex-col h-full">
      {segment === 'exportBuilderAdvanced' && (
        <div className="flex flex-col mb-4">
          <div className="flex gap-2 items-center">
            <Input.Search
              placeholder={t('exportBuilderAdvanced:searchColumnsPlaceholder')}
              allowClear
              value={columnSearchTerm}
              onChange={e => setColumnSearchTerm(e.target.value)}
              style={{ width: 300 }}
              data-testid="search-columns-input"
            />
            <Button
              type="primary"
              data-testid="add-new-column"
              ghost
              onClick={() => setDrawerVisible(true, null)}
              disabled={disabled}
            >
              {t('exportBuilderAdvanced:addNewColumn')}
            </Button>
          </div>
        </div>
      )}
      <div className="flex-1 border border-solid border-gray-400 rounded overflow-x-auto">
        <AutoSizer>
          {({ width, height }) => {
            const allColWidth = filteredColumns.length * COL_WIDTH;
            return (
              <DndContext
                sensors={sensors}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={filteredColumns.map(i => i.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <Table
                    width={allColWidth < width ? width - 2 : allColWidth}
                    height={height - 20}
                    headerHeight={ROW_HEIGHT}
                    rowHeight={ROW_HEIGHT}
                    rowCount={Array.isArray(preview) ? preview.length : 0}
                    rowGetter={getRow}
                  >
                    {filteredColumns.map(col => (
                      <Column
                        key={col.id}
                        label={col.label}
                        dataKey={col.dataKey}
                        columnData={{ id: col.id }}
                        width={COL_WIDTH}
                        headerRenderer={headerRenderer}
                        cellRenderer={cellProps => (
                          <TableBodyCell id={col.id.toString()}>{cellProps.cellData}</TableBodyCell>
                        )}
                      />
                    ))}
                  </Table>
                </SortableContext>
              </DndContext>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};

export default EBPPreviewTable;
